import { createContext, ReactNode, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { useUserUUID } from 'utils/auth';
import { env } from '../../../src/constants';

const loadBraze = async () => {
  return await import('@braze/web-sdk');
};

export type BrazeInstanceType = typeof import('@braze/web-sdk');
export type BrazeContextType = BrazeInstanceType | null | undefined;

export const BrazeContext = createContext<BrazeContextType>(undefined);

interface BrazeProviderProps {
  children: ReactNode;
}

export const BrazeProvider = ({ children }: BrazeProviderProps) => {
  const [brazeInstance, setBrazeInstance] = useState<BrazeContextType>(null);
  const uuid = useUserUUID();
  const { isLoading } = useAuth0();

  const { isEnabled: isPushEnabled, isLoading: isFeatureToggleLoading } =
    useFeatureToggle(FeatureFlag.PRO_TALENT_IS_PUSH_ENABLED);

  useEffect(() => {
    if (shouldInitializeBraze(isFeatureToggleLoading, isLoading, uuid)) {
      const initializeBrazeClient = async () => {
        const braze = await loadBraze();

        braze.initialize(env.VITE_BRAZE_API_KEY, {
          baseUrl: env.VITE_BRAZE_ENDPOINT,
          manageServiceWorkerExternally: true,
          noCookies: true
        });

        if (isPushEnabled && uuid) {
          braze.changeUser(uuid);
        }

        braze.openSession();
        setBrazeInstance(braze);
      };

      void initializeBrazeClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFeatureToggleLoading, uuid]);

  return (
    <BrazeContext.Provider value={brazeInstance}>
      {children}
    </BrazeContext.Provider>
  );
};

const shouldInitializeBraze = (
  isFeatureToggleLoading: boolean,
  isLoading: boolean,
  uuid: string | undefined
) => {
  return !isFeatureToggleLoading && !isLoading && uuid;
};
